/**
 * Single-flight guard for a reload that can be triggered many times in quick
 * succession from a fire-and-forget caller (#4243).
 *
 * The problem it solves: a reactive callback that does `void reload()` on every
 * notification launches a fresh reload without waiting for the previous one. When
 * notifications arrive in a burst — e.g. the DocWatcher broadcast storm during a
 * warm-resume sync — dozens of reloads overlap, each redoing the same async work
 * and mutating the same shared state, which saturates the single JS thread and
 * freezes the page.
 *
 * This guard collapses a burst into at most ONE in-flight run plus ONE trailing
 * catch-up run. While a run is in flight, further calls just mark "run once more
 * when this finishes" (idempotent — many calls still schedule only one re-run), so
 * the final run always reflects the latest request and two runs never overlap.
 *
 * Not a debounce: it does not delay the first run, and it does not batch by time.
 * It serializes overlapping runs and coalesces the surplus into a single trailing
 * run. On a thrown `work`, the error propagates to the caller (so it can surface
 * per the never-swallow rule) and the guard is left usable; a re-run requested
 * during the throwing run is dropped, and the next notification (or the page's
 * ionViewWillEnter resync) re-triggers it.
 */
export class SingleFlightReload {
  private running = false;
  private rerunRequested = false;

  /**
   * Run `work` now if idle; otherwise mark that it must run once more after the
   * in-flight run finishes.
   *
   * Resolution: the FIRST caller (the one that owns the run loop) resolves when
   * its run — and any trailing re-runs it drove — have settled. A FOLDED caller
   * (one that arrived while a run was in flight) resolves immediately, as soon as
   * it has requested the trailing re-run; it does NOT await that re-run. This is
   * fine for the fire-and-forget callers that use it; do not `await` a folded
   * call expecting the reload to be done.
   */
  async runAsync(work: () => Promise<void>): Promise<void> {
    // if a run is already in flight, fold this call into a single trailing re-run
    if (this.running) {
      this.rerunRequested = true;
      return;
    }
    this.running = true;
    try {
      // run, and keep running while a re-run was requested during the last run
      do {
        this.rerunRequested = false;
        await work();
      } while (this.rerunRequested);
    } finally {
      this.running = false;
      this.rerunRequested = false;
    }
  }

  /** True while a run is in flight. */
  get isRunning(): boolean {
    return this.running;
  }
}
